import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Overline } from '@/components/atoms/Eyebrow';
import { Disclosure } from '@/components/molecules/Disclosure';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    ChoiceCards,
    ConfirmRegistration,
    defaultOperationDate,
    FormTextarea,
    PenPicker,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { usePermissions } from '@/contexts/auth';
import { type TimelineFishGroup, useSiteFishGroupActions } from '@/domain/fishGroups';

import { earliestEventDay, eventTimestamp } from '../../helpers/siteActions/actionDate';
import {
    currentStay,
    movableBioscopeIds,
    stayBioscopeIds,
} from '../../helpers/siteActions/groupFacts';
import { moveConfirmItems } from '../../helpers/siteActions/moveConfirm';
import { movePenOptions } from '../../helpers/siteActions/penOptions';
import type { SitePen } from '../../helpers/siteActions/sitePens';
import type { SiteActionKind } from '../FishGroupDetailModal/FishGroupDetailModal';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';
import EventDateField from '../SiteActionDialog/EventDateField';
import GroupSummaryRow from '../SiteActionDialog/GroupSummaryRow';
import PensStatus, { pensKnown, type SitePensState } from '../SiteActionDialog/PensStatus';
import TreatmentFields from '../SiteActionDialog/TreatmentFields';

import styles from '../SiteActionDialog/SiteActionDialog.module.scss';

type BioscopeChoice = 'move' | 'leave';

interface MoveFormValues {
    date: Date | null;
    targetCageId: number | null;
    bioscope: BioscopeChoice;
    treatmentMethod: string;
    treatmentNotes: string;
    notes: string;
}

interface MoveFishGroupModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    pens: readonly SitePen[];
    pensState: SitePensState;
    /** Opens another action for the same group, from the "Use Split / Merge" links. */
    onSwitch: (kind: SiteActionKind) => void;
    onClose: () => void;
    onDone: () => void;
}

/**
 * A whole group into an empty pen of the site (Figma 663:88952): its bioscope can move with
 * it, a delousing can be registered on the same date, and nothing is sent before Confirm.
 */
export default function MoveFishGroupModal(props: MoveFishGroupModalProps) {
    const { clientId, locationId, group, pens, pensState, onSwitch, onClose, onDone } = props;
    const { t } = useTranslation('fishGroups');
    const { canMoveBioscopes } = usePermissions();
    const [withDelousing, setWithDelousing] = useState(false);
    const [confirming, setConfirming] = useState<MoveFormValues | null>(null);
    const actions = useSiteFishGroupActions(clientId, locationId);
    const serverError = useServerErrorMessage(actions.state.error);

    const stay = currentStay(group);
    const fromPen = stay?.cageName ?? '';
    const bioscopeIds = stayBioscopeIds(stay);
    const mayMove = canMoveBioscopes(clientId, locationId);
    const earliest = earliestEventDay(stay?.from ?? null);
    const penLabel = (cageId: number | null) =>
        pens.find((pen) => pen.cageId === cageId)?.displayName ?? '';

    const { handleSubmit, control, setValue, formState } = useForm<MoveFormValues>({
        defaultValues: {
            date: defaultOperationDate(),
            targetCageId: null,
            bioscope: mayMove && bioscopeIds.length > 0 ? 'move' : 'leave',
            treatmentMethod: '',
            treatmentNotes: '',
            notes: '',
        },
    });
    const submitting = formState.isSubmitting || actions.state.isLoading;
    const date = useWatch({ control, name: 'date' });
    const bioscopeChoice = useWatch({ control, name: 'bioscope' });
    const movableAt = (day: Date | null) =>
        movableBioscopeIds(
            stay,
            day ? new Date(eventTimestamp(day, stay?.from ?? null)) : new Date()
        );
    // A bioscope booked into the pen after the move's date isn't there to move yet.
    const movableIds = movableAt(date);
    const mayMoveBioscopes = mayMove && movableIds.length > 0;
    useEffect(() => {
        if (bioscopeChoice === 'move' && !mayMoveBioscopes) setValue('bioscope', 'leave');
    }, [bioscopeChoice, mayMoveBioscopes, setValue]);

    const close = () => {
        if (submitting) return;
        actions.state.reset();
        onClose();
    };

    const register = async (values: MoveFormValues) => {
        if (!values.date || values.targetCageId === null) return;
        const targetCageId = values.targetCageId;
        const moved = await actions
            .move({
                fishGroupId: group.id,
                targetCageId,
                date: eventTimestamp(values.date, stay?.from ?? null),
                notes: values.notes,
                bioscopeMoves:
                    values.bioscope === 'move' && mayMove
                        ? movableAt(values.date).map((bioscopeId) => ({
                              bioscopeId,
                              targetCageId,
                          }))
                        : [],
                ...(withDelousing && {
                    delousing: {
                        treatmentMethod: values.treatmentMethod,
                        notes: values.treatmentNotes,
                    },
                }),
            })
            .then(
                () => true,
                () => false
            );
        if (!moved) return;
        ToastInfo({
            content: t('siteActions.move.successToast', {
                name: group.name,
                pen: penLabel(targetCageId),
            }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'site-fish-group-move-success', autoClose: 5000 },
        });
        actions.state.reset();
        onDone();
    };

    if (confirming?.date) {
        const toPen = penLabel(confirming.targetCageId);
        return (
            <ModalComponent
                isOpen
                size="lg"
                onClose={close}
                aria-label={t('siteActions.confirm.title')}
                backdrop="static"
                body={
                    <ConfirmRegistration
                        fromPens={[fromPen]}
                        toPens={[toPen]}
                        title={t('siteActions.confirm.title')}
                        items={moveConfirmItems(
                            {
                                date: confirming.date,
                                fromPen,
                                toPen,
                                targetBioscopeIds:
                                    pens.find((pen) => pen.cageId === confirming.targetCageId)
                                        ?.bioscopeIds ?? [],
                                bioscopeIds: movableAt(confirming.date),
                                bioscopesMove: confirming.bioscope === 'move' && mayMoveBioscopes,
                                withDelousing,
                            },
                            t
                        )}
                        error={serverError && <p className={styles.serverError}>{serverError}</p>}
                        cancelLabel={t('actions.cancel')}
                        confirmLabel={t('siteActions.confirm.confirm')}
                        isSubmitting={submitting}
                        onCancel={() => {
                            actions.state.reset();
                            setConfirming(null);
                        }}
                        onConfirm={() => register(confirming)}
                    />
                }
            />
        );
    }

    const switchLink = (kind: SiteActionKind, label: string) => (
        <Button variant="link" className={styles.inlineLink} onClick={() => onSwitch(kind)}>
            {label}
        </Button>
    );

    const body = (
        <form className={styles.form} onSubmit={handleSubmit((values) => setConfirming(values))}>
            <EventDateField control={control} name="date" earliest={earliest} />
            <section className={styles.section}>
                <Overline as="h3" className={styles.sectionLabel}>
                    {t('siteActions.move.groupToMove')}
                </Overline>
                <GroupSummaryRow group={group} />
            </section>
            <section className={styles.section}>
                <Overline as="h3" className={styles.sectionLabel}>
                    {t('siteActions.form.destinationPen')}
                </Overline>
                <PensStatus state={pensState} />
                <Controller
                    control={control}
                    name="targetCageId"
                    rules={{ validate: (value) => value !== null }}
                    render={({ field, fieldState }) => (
                        <>
                            <PenPicker
                                options={movePenOptions(pens, stay?.cageId ?? null, t)}
                                value={field.value}
                                onChange={field.onChange}
                                aria-label={t('siteActions.form.destinationPen')}
                            />
                            {fieldState.error && (
                                <p className={styles.serverError} role="alert">
                                    {t('siteActions.form.pickPen')}
                                </p>
                            )}
                        </>
                    )}
                />
                <Alert tone="info" size="small">
                    {t('siteActions.move.onlyEmptyPens')} {t('siteActions.move.partOfTheFish')}{' '}
                    {switchLink('split', t('siteActions.move.useSplit'))}.{' '}
                    {t('siteActions.move.withAnotherGroup')}{' '}
                    {switchLink('merge', t('siteActions.move.useMerge'))}.
                </Alert>
            </section>
            {movableIds.length > 0 && (
                <section className={styles.section}>
                    <Overline as="h3" className={styles.sectionLabel}>
                        {t('siteActions.form.bioscope')}
                    </Overline>
                    <Controller
                        control={control}
                        name="bioscope"
                        render={({ field }) => (
                            <ChoiceCards
                                aria-label={t('siteActions.form.bioscope')}
                                value={field.value}
                                onChange={field.onChange}
                                options={[
                                    ...(mayMoveBioscopes
                                        ? [
                                              {
                                                  value: 'move',
                                                  label: t('siteActions.move.bioscopeMove', {
                                                      ids: movableIds.join(', '),
                                                  }),
                                                  description: t(
                                                      'siteActions.move.bioscopeMoveHint'
                                                  ),
                                              },
                                          ]
                                        : []),
                                    {
                                        value: 'leave',
                                        label: t('siteActions.move.bioscopeLeave', {
                                            ids: movableIds.join(', '),
                                            pen: fromPen,
                                        }),
                                        description: t('siteActions.move.bioscopeLeaveHint'),
                                    },
                                ]}
                            />
                        )}
                    />
                </section>
            )}
            <Disclosure
                variant="inline"
                title={t('siteActions.move.addDelousing')}
                open={withDelousing}
                onOpenChange={setWithDelousing}>
                <TreatmentFields
                    control={control}
                    methodName="treatmentMethod"
                    notesName="treatmentNotes"
                />
            </Disclosure>
            <FormTextarea
                control={control}
                name="notes"
                label={t('siteActions.form.notes')}
                placeholder={t('siteActions.move.notesPlaceholder')}
                optional
            />
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('siteActions.move.submit')}
                isSubmitting={submitting}
                submitDisabled={!pensKnown(pensState)}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={
                <ActionDialogTitle
                    kind="move"
                    title={t('siteActions.move.title')}
                    groupName={group.name}
                    penName={stay?.cageName ?? null}
                    bioscopeIds={bioscopeIds}
                />
            }
            body={body}
            backdrop="static"
        />
    );
}
